import { MarketData } from '../../utils/types';

/**
 * Exportadoras seleccionadas en Mercado Diario: un punto por exportadora en
 * vez de un promedio de todas. Compartido por DailyMarketChart y
 * VarietyPriceChart para que los dos las muestren igual en gráfico y tabla.
 */

/** Colores distintos del verde propio, las etiquetas, el mercado y Bitácora. */
export const EXPORTER_PALETTE = [
  '#2196F3',
  '#3F51B5',
  '#795548',
  '#607D8B',
  '#AFB42B',
  '#03A9F4',
  '#1A237E',
  '#BCAAA4',
  // Los rosados al final: se confunden con el rojo del mercado.
  '#F06292',
  '#E91E63',
];

export interface ExporterPoint {
  x: string;
  y: number | null;
  percentile: number | null;
  records: number;
}

export interface ExporterSeries {
  name: string;
  type: 'scatter';
  data: ExporterPoint[];
  color: string;
  isExporterSeries: true;
}

/** Clave de una etiqueta de otra exportadora al separar por etiqueta. */
export const exporterBrandKey = (exporter: string, brand?: string | null) =>
  `${exporter}::${brand || ''}`;

/** Para encontrar las series de exportadoras entre las demás del gráfico. */
export const isExporterSeries = (series: unknown): series is ExporterSeries =>
  typeof series === 'object' &&
  series !== null &&
  (series as ExporterSeries).isExporterSeries === true;

interface BuildExporterSeriesArgs {
  records: MarketData[];
  categories: string[];
  /** Categoría (calibre o variedad) del registro, o null si no va. */
  categoryOf: (item: MarketData) => string | null;
  /** Precios de todo el mercado por categoría, para el percentil. */
  marketPrices: Record<string, number[]>;
  /** La exportadora del usuario ya tiene su propio punto. */
  excludeExporter?: string;
  /**
   * Al separar por etiqueta: las etiquetas (exporterBrandKey) a mostrar,
   * un punto por cada una. Sin esto, un punto por exportadora.
   */
  brandKeys?: string[] | null;
  noBrandLabel?: string;
}

/** Una serie por exportadora, ordenadas por cantidad de registros. */
export const buildExporterSeries = ({
  records,
  categories,
  categoryOf,
  marketPrices,
  excludeExporter,
  brandKeys,
  noBrandLabel = '',
}: BuildExporterSeriesArgs): ExporterSeries[] => {
  const pricesByExporter: Record<string, Record<string, number[]>> = {};
  const counts: Record<string, number> = {};
  const names: Record<string, string> = {};
  const selectedKeys = brandKeys ? new Set(brandKeys) : null;

  records.forEach((item) => {
    if (!item.exporter || item.exporter === excludeExporter) return;
    let key = item.exporter;
    if (selectedKeys) {
      key = exporterBrandKey(item.exporter, item.brand);
      if (!selectedKeys.has(key)) return;
      names[key] = `${item.exporter} · ${item.brand || noBrandLabel}`;
    }
    // El orden (y el color) sale de todos los registros, para que cada
    // exportadora tenga el mismo color en los dos gráficos.
    counts[key] = (counts[key] || 0) + 1;
    const category = categoryOf(item);
    if (category === null) return;
    if (!pricesByExporter[key]) pricesByExporter[key] = {};
    const byCategory = pricesByExporter[key];
    if (!byCategory[category]) byCategory[category] = [];
    byCategory[category].push(item.price_kg);
  });

  return Object.keys(counts)
    .sort((a, b) => counts[b] - counts[a] || a.localeCompare(b))
    .map((exporter, index) => ({ exporter, index }))
    .filter(({ exporter }) => pricesByExporter[exporter])
    .map(({ exporter, index }) => ({
      name: (names[exporter] || exporter).toUpperCase(),
      type: 'scatter' as const,
      color: EXPORTER_PALETTE[index % EXPORTER_PALETTE.length],
      isExporterSeries: true as const,
      data: categories.map((category) => {
        const prices = pricesByExporter[exporter][category] || [];
        if (prices.length === 0) {
          return { x: category, y: null, percentile: null, records: 0 };
        }
        const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
        const market = marketPrices[category] || [];
        const percentile =
          market.length > 0
            ? (market.filter((price) => price <= avg).length / market.length) *
              100
            : null;
        return { x: category, y: avg, percentile, records: prices.length };
      }),
    }));
};
